/**
 * Client-side template resolver for the LIVE PREVIEW. Walks the same marker tree with the same answer
 * keys and the same semantics as the authoritative backend resolver
 * (backend/Reeve.Core/Templates/TemplateEngine.cs): an absent IF answer is false, a CHOOSE defaults to
 * its first branch, and CHOOSE/REPEAT nodes get stable pre-order keys `choice:{n}` / `repeat:{n}`.
 * Change the walk there and this must change with it, or the preview drifts from the generated document.
 *
 * Output is a list of preview BLOCKS: markdown runs carrying inline sentinels (`{idx}`) that
 * index into `meta` (a filled value, or a gap), plus standalone DRAFT blocks so the preview can tint
 * LLM-written passages differently from verified template text. The same walk collects the ACTIVE
 * question list (a question is active exactly when its node is reached), in document order, grouped by
 * the nearest heading — which is what makes the right-hand question column stay in step with the
 * document with no separate visibility machinery.
 */

import type { TemplateDefinition, TemplateNode } from './entities/templates';

/** Inline dynamic content inside a markdown run: a filled value or an unfilled gap. */
export interface PreviewMeta {
  /** The answer key the span belongs to (a placeholder name, or a repeat row field). */
  key: string;
  kind: 'value' | 'gap';
  /** The text to render: the value itself, or the gap's human label. */
  text: string;
}

export type PreviewBlock =
  | { type: 'md'; text: string }
  | {
      type: 'draft';
      /** The draft note id, e.g. "D1". */
      id: string;
      /** The answer key, `draft:{id}`. */
      key: string;
      /** The passage text (LLM-drafted or user-written), or null while unwritten. */
      text: string | null;
      title: string;
    };

export type QuestionKind =
  | 'boolean'
  | 'choice'
  | 'draft'
  | 'repeat'
  | 'text'
  | 'textarea'
  | 'date'
  | 'money'
  | 'number'
  | 'email';

/** One active question for the right-hand column. */
export interface ActiveQuestion {
  key: string;
  kind: QuestionKind;
  /** The document section (nearest heading) the question belongs to. */
  section: string;
  prompt: string;
  help?: string;
  example?: string;
  /** choice only: one option per branch. */
  options?: { label: string; preview: string }[];
  /** repeat only: the fields collected per row. */
  itemFields?: { name: string; label: string; whatToEnter: string; example: string; kind: string }[];
}

export interface ResolvedTemplate {
  blocks: PreviewBlock[];
  meta: PreviewMeta[];
  questions: ActiveQuestion[];
  /** Keys of active questions still needing an answer (empty field or unwritten draft). */
  missing: string[];
}

/** Mirrors the backend's Truthy: "true"/"yes"/"1", case-insensitive. */
function truthy(v: string | undefined): boolean {
  return v !== undefined && (v.toLowerCase() === 'true' || v.toLowerCase() === 'yes' || v === '1');
}

function humanize(upperSnake: string): string {
  return upperSnake.split('_').map((w) => w.toLowerCase()).join(' ');
}

/**
 * Pre-order key assignment for CHOOSE (`choice:{n}`) and REPEAT (`repeat:{n}`) nodes — the exact
 * traversal the backend uses, so answer keys can never drift between preview and generation.
 */
function assignMarkerKeys(body: TemplateNode[]): Map<TemplateNode, string> {
  const map = new Map<TemplateNode, string>();
  let choose = 0;
  let repeat = 0;
  const visit = (nodes: TemplateNode[]) => {
    for (const n of nodes) {
      switch (n.t) {
        case 'choose':
          map.set(n, `choice:${choose++}`);
          for (const b of n.c ?? []) visit(b.c ?? []);
          break;
        case 'repeat':
          map.set(n, `repeat:${repeat++}`);
          visit(n.c ?? []);
          break;
        case 'if':
        case 'locked':
          visit(n.c ?? []);
          break;
        default:
          break;
      }
    }
  };
  visit(body);
  return map;
}

/** Resolve the tree against the current answers into preview blocks + the active question list. */
export function resolveTemplate(
  def: TemplateDefinition,
  values: Record<string, string>,
  repeats: Record<string, Record<string, string>[]>,
): ResolvedTemplate {
  const keys = assignMarkerKeys(def.body);
  const blocks: PreviewBlock[] = [];
  const meta: PreviewMeta[] = [];
  const questions: ActiveQuestion[] = [];
  const seen = new Set<string>();
  const missing: string[] = [];
  let buffer = '';
  let section = '';

  const flush = () => {
    if (buffer.length > 0) {
      blocks.push({ type: 'md', text: buffer });
      buffer = '';
    }
  };

  const addQuestion = (q: ActiveQuestion) => {
    if (seen.has(q.key)) return;
    seen.add(q.key);
    questions.push(q);
  };

  const fieldLabel = (name: string) => def.fields[name]?.label ?? humanize(name);

  const addFieldQuestion = (name: string) => {
    const f = def.fields[name];
    addQuestion({
      key: name,
      kind: (f?.kind ?? 'text') as QuestionKind,
      section,
      prompt: fieldLabel(name),
      help: f?.whatToEnter || undefined,
      example: f?.example || undefined,
    });
  };

  // Section tracking works on COMPLETED LINES assembled across nodes, because a heading that contains
  // a placeholder is split over several nodes ("## About " + ph + newline) and a per-node regex would
  // truncate the title ("About" instead of "About Northgate Joinery Ltd").
  let lineAcc = '';
  const HEADING_LINE = /^#{1,6}\s+(.+?)\s*$/;
  const completeLine = () => {
    const m = HEADING_LINE.exec(lineAcc);
    if (m) {
      const cleaned = m[1].replace(/\*\*|\*|\|/g, ' ').replace(/\s+/g, ' ').trim();
      if (cleaned.length > 0) section = cleaned;
    }
    lineAcc = '';
  };
  const accumulate = (text: string) => {
    const parts = text.split('\n');
    for (let i = 0; i < parts.length - 1; i += 1) {
      lineAcc += parts[i];
      completeLine();
    }
    lineAcc += parts[parts.length - 1];
  };

  /** True when the current output line is a table row, so a multi-line value must not break the table. */
  const inTableRow = () => {
    const lineStart = buffer.lastIndexOf('\n') + 1;
    return buffer.slice(lineStart).trimStart().startsWith('|');
  };

  const emitValue = (key: string, value: string | undefined, gapLabel: string) => {
    const filled = value !== undefined && value.trim().length > 0;
    // Inside a pipe-table row a newline would end the row and corrupt the table (in the generated
    // document too — the backend applies the same normalization), so fold multi-line values there.
    const text = filled
      ? inTableRow()
        ? value!.replace(/\s*\n+\s*/g, '; ')
        : value!
      : gapLabel;
    const idx = meta.push({ key, kind: filled ? 'value' : 'gap', text }) - 1;
    buffer += `${idx}`;
    accumulate(text);
  };

  const walk = (
    nodes: TemplateNode[],
    item: Record<string, string> | null,
    repeatFields: ActiveQuestion['itemFields'] | null,
  ) => {
    for (const n of nodes) {
      switch (n.t) {
        case 'text':
          if (n.v) {
            buffer += n.v;
            accumulate(n.v);
          }
          break;

        case 'ph': {
          const name = n.n!;
          if (repeatFields) {
            if (!repeatFields.some((f) => f.name === name)) {
              const f = def.fields[name];
              repeatFields.push({
                name,
                label: fieldLabel(name),
                whatToEnter: f?.whatToEnter ?? '',
                example: f?.example ?? '',
                kind: f?.kind ?? 'text',
              });
            }
          } else {
            addFieldQuestion(name);
          }
          // Mirror the backend's Value(): a repeat row's own value first, then the scalar answers.
          const itemValue = item?.[name];
          const value = itemValue !== undefined && itemValue.trim().length > 0 ? itemValue : values[name];
          emitValue(name, value, fieldLabel(name));
          break;
        }

        // IF, CHOOSE and DRAFT questions are registered even inside a repeat: their answer keys are
        // GLOBAL scalars (`if:X`, `choice:{n}`, `draft:Dn`), so without a card the user could never set
        // them and the gated content would be locked off. Only PLACEHOLDERS become per-row item fields.
        // The `seen` set dedupes across rows.
        case 'if': {
          const key = `if:${n.m}`;
          addQuestion({
            key,
            kind: 'boolean',
            section,
            prompt: def.conditions[n.m!]?.question ?? `Does this apply: ${humanize(n.m!)}?`,
          });
          if (truthy(values[key])) walk(n.c ?? [], item, repeatFields);
          break;
        }

        case 'choose': {
          const key = keys.get(n)!;
          const branches = n.c ?? [];
          if (branches.length === 0) break;
          addQuestion({
            key,
            kind: 'choice',
            section,
            prompt: section.length > 0 ? `${section}: choose the wording that applies` : 'Choose the wording that applies',
            options: branches.map((b) => ({ label: b.label ?? 'This option', preview: b.preview ?? '' })),
          });
          const raw = values[key];
          const parsed = raw === undefined ? 0 : Number.parseInt(raw, 10);
          const idx = Number.isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), branches.length - 1);
          walk(branches[idx].c ?? [], item, repeatFields);
          break;
        }

        case 'branch':
        case 'locked':
          walk(n.c ?? [], item, repeatFields);
          break;

        case 'draft': {
          const id = n.id!;
          const key = `draft:${id}`;
          const note = def.drafts[id];
          addQuestion({
            key,
            kind: 'draft',
            section,
            prompt: note?.title && note.title.length > 0 ? note.title : 'Write this passage',
            help: note?.guidance || undefined,
            example: note?.example || undefined,
          });
          const text = values[key];
          flush();
          blocks.push({
            type: 'draft',
            id,
            key,
            text: text && text.trim().length > 0 ? text : null,
            title: note?.title && note.title.length > 0 ? note.title : humanize(id),
          });
          break;
        }

        case 'repeat': {
          const key = keys.get(n)!;
          const itemFields: NonNullable<ActiveQuestion['itemFields']> = [];
          const rows = repeats[key];
          if (rows && rows.length > 0) {
            for (const row of rows) walk(n.c ?? [], row, itemFields);
          } else {
            walk(n.c ?? [], {}, itemFields);
          }
          if (!repeatFields) {
            addQuestion({
              key,
              kind: 'repeat',
              section,
              prompt: n.note && n.note.length > 0 ? n.note : 'Add each row',
              itemFields,
            });
          }
          break;
        }

        default:
          break;
      }
    }
  };

  walk(def.body, null, null);
  flush();

  // What still needs an answer: every active field with no value, every active draft with no text.
  // Booleans and choices always carry a value (seeded); repeats never block (a register may start empty).
  for (const q of questions) {
    if (q.kind === 'boolean' || q.kind === 'choice' || q.kind === 'repeat') continue;
    const v = values[q.key];
    if (v === undefined || v.trim().length === 0) missing.push(q.key);
  }

  return { blocks, meta, questions, missing };
}
